import { useQueryClient } from '@tanstack/react-query';

import {
  getApiV1LocationCountriesCountryidLangsQueryOptions,
  usePostApiV1LocationCountriesCountryidLangs,
  usePutApiV1LocationCountriesCountryidLangsId,
} from '@/shared/api/generated';
import type { CountryLangFormValues } from '../types/countryLangForm';

/** Callbacks do salvamento de idioma. */
export interface SaveCountryLangCallbacks {
  readonly onSuccess?: () => void;
  readonly onError?: () => void;
}

/**
 * Hook para adicionar/editar um idioma de um país.
 *
 * Encapsula os mutations gerados pelo Kubb (`POST`/`PUT` de
 * `/api/v1/location/countries/{countryId}/langs`), escolhendo a operação pela
 * presença de `id` e invalidando o cache da listagem no sucesso.
 *
 * @param countryId - Id do país dono dos idiomas.
 */
export function useSaveCountryLang(countryId: number) {
  const queryClient = useQueryClient();
  const createMutation = usePostApiV1LocationCountriesCountryidLangs();
  const updateMutation = usePutApiV1LocationCountriesCountryidLangsId();

  const invalidate = () =>
    queryClient.invalidateQueries(getApiV1LocationCountriesCountryidLangsQueryOptions(countryId));

  /** Salva um idioma. Cria quando `id` é `undefined`; caso contrário, atualiza. */
  const save = (
    values: CountryLangFormValues,
    id: number | undefined,
    callbacks?: SaveCountryLangCallbacks,
  ) => {
    const data = { cultureInfo: values.cultureInfo, name: values.name, active: values.active };
    const onSuccess = () => {
      void invalidate();
      callbacks?.onSuccess?.();
    };
    const onError = () => callbacks?.onError?.();

    if (id === undefined) {
      createMutation.mutate({ countryId, data }, { onSuccess, onError });
    } else {
      updateMutation.mutate({ countryId, id, data }, { onSuccess, onError });
    }
  };

  return {
    save,
    isPending: createMutation.isPending || updateMutation.isPending,
  } as const;
}
